/**
 * Captures the visitor's original referrer + UTM parameters once per browser
 * session, so subsequent in-app navigations still report the true source.
 *
 * Returned object is meant to be spread into the body of `track-content-view`
 * calls. The edge function classifies `referrer` into `referrer_source`
 * (google / bing / chatgpt / social / direct / etc.).
 */

const STORAGE_KEY = 'r0hub:visitor_source';

export interface VisitorSource {
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_path?: string;
}

function captureNow(): VisitorSource {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const source: VisitorSource = {
      referrer: document.referrer || undefined,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      landing_path: window.location.pathname + (window.location.search || ''),
    };
    // Drop self-referrals (same-origin) so we keep the true upstream source.
    if (source.referrer) {
      try {
        const refHost = new URL(source.referrer).host;
        if (refHost === window.location.host) {
          source.referrer = undefined;
        }
      } catch { /* malformed referrer */ }
    }
    return source;
  } catch {
    return {};
  }
}

export function getVisitorSource(): VisitorSource {
  if (typeof window === 'undefined') return {};
  try {
    // Prefer first-touch (localStorage) so attribution survives across sessions/devices.
    const persisted = localStorage.getItem(STORAGE_KEY);
    if (persisted) return JSON.parse(persisted) as VisitorSource;
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) return JSON.parse(cached) as VisitorSource;
    const captured = captureNow();
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(captured)); } catch { /* ignore */ }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    return captured;
  } catch {
    return captureNow();
  }
}
